import { FC, KeyboardEvent, memo, useEffect } from "react";
import { Link } from "react-router-dom";
import { HiViewGrid, HiChevronLeft, HiChevronRight } from "react-icons/hi";
import Accordion from 'react-bootstrap/Accordion';
import { useTranslation } from "react-i18next";

import classes from "./styles.module.css";
import { useCatsQuery } from "src/store/api/cats/catsApiSlice";
import { PRIMARY } from "src/constants/colors";
import { ElementLoading, EmptyElement } from "src/components";
import { CategoryType } from "./types";
import { isRTL } from "src/utils/global";
import { useError, useUpdateEffect } from "src/hooks";
import { TbShoppingBagSearch } from 'react-icons/tb';


interface CatsMenuProp {
    openMenu: boolean;
    setOpenMenu: any;
}


const CatsMenu = ({ openMenu, setOpenMenu }: CatsMenuProp) => {
    const { t, i18n } = useTranslation("common");
    const { data, isLoading, isSuccess, isFetching, refetch, isError, error } = useCatsQuery(undefined, { refetchOnMountOrArgChange: true, refetchOnReconnect: true });
    const closeMenuHandle = () => { setOpenMenu(false); };
    const { showError } = useError();

    useEffect(() => {
        if (isError) {
            showError(error);
        }
    }, [error, isError, showError]);

    useEffect(() => {
        if (!openMenu) {
            document.body.style.overflow = '';
            return;
        }

        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, [openMenu]);

    useUpdateEffect(refetch, [i18n.language]);

    const handleMenuKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === "Escape") {
            closeMenuHandle();
        }
    };

    return (
        <section className={`${classes.cats__con} ${openMenu ? classes.cats__con__open : ''} ${isRTL() && classes.rtl} `} onKeyDown={handleMenuKeyDown}>
            <div aria-hidden={!openMenu} aria-modal="true" className={`${classes.cats__inline__con} ${openMenu ? classes.cats__inline__open : ''}`} role="dialog">
                <header className={classes.header}>
                    <div className={classes.title__con}>
                        <HiViewGrid size={28} color={PRIMARY} />
                        <h4>
                            {t("nav.categories.title")}
                        </h4>
                    </div>
                    <button aria-label={t("nav.categories.close")} className={classes.close__con} onClick={closeMenuHandle} type="button">
                        {isRTL() ? <HiChevronRight size={28} /> :
                            <HiChevronLeft size={28} />
                        }
                    </button>
                </header>
                <div className={classes.cats}>
                    {
                        isSuccess && !isLoading && !isFetching ?
                            data.length > 0 ?
                                <Accordion defaultActiveKey={`${data.length > 0 ? data[0].id : null}`}>
                                    {data.map((cat: CategoryType) => (
                                        <Category
                                            key={`cat__${cat.id}`}
                                            cat={cat}
                                            lang={i18n.language}
                                            closeMenu={closeMenuHandle}  // 👈 pass close handler
                                        />
                                    ))}
                                </Accordion> :
                                <div className={classes.empty__con}>
                                    <EmptyElement
                                        variant="inline"
                                        icon={<TbShoppingBagSearch />}
                                        title={t("null_items")}
                                    />
                                </div>
                            : isError ? (
                                <div className={classes.error__con}>
                                    <p>{t("errors.server")}</p>
                                    <button className="btn__primary" onClick={() => refetch()} type="button">
                                        {t("errors.screen.btn")}
                                    </button>
                                </div>
                            )
                            :
                            <ElementLoading />
                    }
                </div>
            </div>
            <div className={`${classes.overlay} ${openMenu ? classes.overlay__open : ''}`}
                onClick={closeMenuHandle}>
            </div>

        </section>
    )
}

export default memo(CatsMenu);


interface CategoryProps {
    cat: CategoryType,
    lang: string,
    closeMenu: () => void
}

const Category: FC<CategoryProps> = ({ cat, lang, closeMenu }) => {
    return cat.children.length > 0 ? (
        <Accordion.Item eventKey={`${cat.id}`} className={classes.accordion__item}>
            <Accordion.Header>
                {lang.includes('ar') ? cat.name_ar : cat.name_en}
            </Accordion.Header>
            <Accordion.Body className={classes.accordion__body}>
                <ul className={classes.cat__links}>
                    {cat.children.map((sub_cat) => (
                        <li key={`sub_cat__${sub_cat.id}`}>
                            <Link
                                to={`/categories/${sub_cat.slug}/products/`}
                                className={classes.cat__menu__link}
                                title={lang.includes('ar') ? sub_cat.name_ar : sub_cat.name_en}
                                onClick={closeMenu}  // 👈 close menu here
                            >
                                {lang.includes('ar') ? sub_cat.name_ar : sub_cat.name_en}
                            </Link>
                        </li>
                    ))}
                </ul>
            </Accordion.Body>
        </Accordion.Item>
    ) : (
        <div className={classes.cat__link__con}>
            <Link
                to={`/categories/${cat.slug}/products/`}
                className={classes.cat__link}
                title={lang.includes('ar') ? cat.name_ar : cat.name_en}
                onClick={closeMenu}  // 👈 close menu here
            >
                {lang.includes('ar') ? cat.name_ar : cat.name_en}
            </Link>
        </div>
    );
};
